import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn, AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import { useClerkErrorHandler } from './hooks/useClerkErrorHandler';
import { ThemeProvider } from './styles/ThemeProvider';
import { Toaster } from 'sonner';

// Importar todos los componentes necesarios
import Landing from './pages/Landing';
import SignIn from './pages/SignIn';
import Dashboard from './pages/Dashboard';
import AiChatPage from './ai-chat/AiChatPage';
import OAuthConsent from './pages/oauth/OAuthConsent';
import OAuthCallback from './pages/oauth/OAuthCallback';

// Conversiones pages
import ConversionesDashboard from './pages/conversiones';
import ProspectosFreelancerPage from './pages/conversiones/prospectos-freelancer/page';
import ConversionesVistas from './pages/conversiones/vistas/page';
import ConversionesSesiones from './pages/conversiones/sesiones/page';
import ConversionesSimulaciones from './pages/conversiones/simulaciones/page';
import ConversionesContrataciones from './pages/conversiones/contrataciones/page';
import ConversionesSuscripciones from './pages/conversiones/suscripciones/page';
import ConversionesCertificados from './pages/conversiones/certificados/page';
import ConversionesMobileHome from './pages/conversiones/mobile/page';
import ConversionesMobileStage from './pages/conversiones/mobile/stage/page';

// Admin pages
import FamiliasList from './pages/admin/FamiliasList';
import FamiliasForm from './pages/admin/FamiliasForm';
import PlanesList from './pages/admin/PlanesList';
import PlanesForm from './pages/admin/PlanesForm';
import UfDiariaList from './pages/admin/UfDiariaList';
import UfDiariaForm from './pages/admin/UfDiariaForm';
import RamosList from './pages/admin/RamosList';
import RamosForm from './pages/admin/RamosForm';
import UrlsList from './pages/admin/UrlsList';
import UrlsForm from './pages/admin/UrlsForm';

// Payment pages
import PaymentsList from './pages/payments/PaymentsList';
import SubscriptionsList from './pages/payments/SubscriptionsList';

// VirtualPos pages - UPDATED IMPORTS
import VirtualPosPlansList from './pages/payments/virtualpos/plans/VirtualPosPlansList';
import VirtualPosPlansForm from './pages/payments/virtualpos/plans/VirtualPosPlansForm';
import VirtualPosPlanDetail from './pages/payments/virtualpos/plans/VirtualPosPlanDetail';

// VirtualPos Subscriptions - NEW IMPORTS
import VirtualPosSubscriptionsList from './pages/payments/virtualpos/subscriptions/VirtualPosSubscriptionsList';
import VirtualPosSubscriptionForm from './pages/payments/virtualpos/subscriptions/VirtualPosSubscriptionForm';
import VirtualPosSubscriptionDetail from './pages/payments/virtualpos/subscriptions/VirtualPosSubscriptionDetail';
import SubscriptionChargesList, { SubscriptionChargeDetailPage } from './pages/payments/cargos/SubscriptionChargesList';
import CronRunLogs from './pages/payments/cron/CronRunLogs';
import ConciliacionList from './pages/payments/conciliacion/ConciliacionList.jsx';
import FacturaUpload from './pages/payments/conciliacion/FacturaUpload.jsx';

// YoolPay pages — modelo financiero (finance.movements, finance.cxc_aging)
import YoolPayMes from './pages/payments/yoolpay/YoolPayMes';
import YoolPayDia from './pages/payments/yoolpay/YoolPayDia';
import YoolPayFeeDefinitionsList from './pages/payments/yoolpay/YoolPayFeeDefinitionsList';
import InformeLiquidacion from './pages/payments/yoolpay/InformeLiquidacion';

// Subscription Manager
// ELIMINAR esta línea:
// import SubscriptionManager from './components/payments/SubscriptionManager';

// User pages
import UsersList from './pages/users/UsersList';
import ClientsList from './pages/clientes/ClientesList';

// Contratantes pages
import ContratantesList from './pages/contratantes/ContratantesList';
import ContratantesManager from './pages/contratantes/ContratantesManager';

// Trazabilidad
import AuditoriaList from './pages/trazabilidad/AuditoriaList';

// Certificate pages
import CertificatesList from './pages/certificates/CertificatesList';
import RendicionesPage from './pages/emision/RendicionesPage';
import PolizasIndividualesList from './pages/emision/PolizasIndividualesList';
import WalletPasses from './pages/wallet/WalletPasses';
import WalletBroadcast from './pages/wallet/WalletBroadcast';
import PdfTemplateManager from './pages/pdf/PdfTemplateManager';

// Product config pages
import DivinaProductsList from './pages/product_config/ProductsList';
import DivinaProductsForm from './pages/product_config/ProductsForm';
import DivinaPlanesList from './pages/product_config/PlanesList';
import DivinaPlanesForm from './pages/product_config/PlanesForm';
import ColectivePolizasList from './pages/product_config/colectivos/ColectivePolizasList';
import PolizaColectivaWizard from './pages/product_config/colectivos/PolizaColectivaWizard';
import ContratantesColectivosList from './pages/product_config/colectivos/ContratantesColectivosList';
import ContratantesColectivosForm from './pages/product_config/colectivos/ContratantesColectivosForm';

// Editorial pages — historias (contenido publicado en flow)
import HistoriasList from './pages/editorial/HistoriasList';
import HistoriasForm from './pages/editorial/HistoriasForm';


// Órdenes de Pago
import OrdenespagoList from './pages/ordenes-pago/OrdenespagoList';
import OrdenespagoDetails from './pages/ordenes-pago/OrdenespagoDetails';

// Companies pages
import CompaniesList from './pages/companies/CompaniesList';
import CompanyForm from './pages/companies/CompanyForm';
import EmpresasPlanesManager from './pages/empresas/EmpresasPlanesManager';
import CompanyDashboard from './pages/companies/dashboard';

// App Health pages
import AppHealth from './pages/app-health';

// Other pages
import Exportar from './pages/Exportar';
import ErrorPage from './pages/ErrorPage';
import Settings from './pages/Settings';
import FileMakerTest from './components/FileMakerTest';

import EmailClient from './pages/email/EmailClient';
import InstagramInsightsPage from './pages/socials/instagram/insights/page';

// Campaigns pages
import CampaignsList from './pages/campaigns/CampaignsList';
import CampaignsForm from './pages/campaigns/CampaignsForm';
import CampaignDetail from './pages/campaigns/id/page';
import CampaignsFormEdit from './pages/campaigns/id/edit/page';
import TemplatesList from './pages/campaigns/templates/page';
import TemplatesForm from './pages/campaigns/templates/new/page';
import TemplatePreview from './pages/campaigns/templates/id/page';
import TemplatesFormEdit from './pages/campaigns/templates/id/edit/page';
import ContactsList from './pages/campaigns/contacts/page';
import ContactsForm from './pages/campaigns/contacts/new/page';
import ContactsFormEdit from './pages/campaigns/contacts/id/edit/page';
import ContactDetail from './pages/campaigns/contacts/id/page';
import SegmentsList from './pages/campaigns/segments/page';
import SegmentsForm from './pages/campaigns/segments/new/page';
import SegmentDetail from './pages/campaigns/segments/id/page';
import SegmentsFormEdit from './pages/campaigns/segments/id/edit/page';
import ContactSegmentManager from './pages/campaigns/contacts/id/segments/page';
import SingleMailForm from './pages/campaigns/single/page';

// Marketing Pages
import ContentAgent from './pages/ContentAgent';
import LeadQualifier from './pages/LeadQualifier';

// Conversaciones pages
import ConversacionesList from './pages/conversaciones/ConversacionesList';
import ConversacionDetalle from './pages/conversaciones/ConversacionDetalle';

// Dev pages
const ApiDocsPage = lazy(() => import('./pages/dev/api-docs/page'));

const resolveComponentExport = (moduleCandidate) => {
  let current = moduleCandidate;

  for (let i = 0; i < 6; i += 1) {
    if (typeof current === 'function') return current;
    if (!current || typeof current !== 'object') break;

    current =
      current.default ??
      current.ConversationalSalesAgent ??
      Object.values(current)[0];
  }

  return () => null;
};

function ConversationalSalesAgentRoute() {
  const [ResolvedComponent, setResolvedComponent] = useState(null);

  useEffect(() => {
    let isMounted = true;

    import('./pages/ConversationalSalesAgent/index.jsx')
      .then((module) => {
        if (!isMounted) return;
        setResolvedComponent(() => resolveComponentExport(module));
      })
      .catch(() => {
        if (!isMounted) return;
        setResolvedComponent(() => () => null);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!ResolvedComponent) return null;

  return <ResolvedComponent />;
}

/**
 * Ruta que exige sesión: con ella renderiza el contenido, sin ella manda al login.
 *
 * `<SignedIn>` por sí solo NO redirige — sin sesión no renderiza nada y la ruta
 * queda en blanco, sin login ni aviso. El intento previo de cubrirlo fue un
 * `<Route path="*">` con RedirectToSignIn, pero un catch-all solo corre cuando
 * ninguna otra ruta matchea, y una ruta definida siempre gana: nunca se ejecutó.
 * Le pasa a cualquiera que abra un link directo con la sesión vencida.
 */
/**
 * Manda el detalle de un cargo de la ruta vieja a la nueva SIN perder el identificador:
 * un enlace guardado a un cargo puntual tiene que llegar a ese cargo, no a la lista.
 */
function RedirectCargoDetalle() {
  const { id } = useParams();
  return <Navigate to={`/payments-admin/cargos/${id}`} replace />;
}

function RutaProtegida({ children }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

// Componente interno que usa el hook
function AppContent() {
  const { reportClerkError } = useClerkErrorHandler();
  
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Ruta pública - Landing */}
          <Route path="/" element={<Landing />} />
          {/* Acceso (YoolAuthSplit del design system) */}
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-in/sso-callback" element={<AuthenticateWithRedirectCallback signInFallbackRedirectUrl="/dashboard" />} />
          <Route path="/oauth/consent" element={<OAuthConsent />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          {/* OAuth callback */}
          
          {/* Rutas protegidas */}
          <Route path="/dashboard" element={
            <RutaProtegida>
              <Layout>
                <Dashboard />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/yoolia" element={
            <RutaProtegida>
              <Layout>
                <AiChatPage />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/ai-chat" element={<Navigate to="/yoolia" replace />} />

          <Route path="/conversiones" element={
            <RutaProtegida>
                <Layout>
                  <ConversionesDashboard />
                </Layout>
              </RutaProtegida>
          } />

          <Route path="/conversiones/m" element={
            <RutaProtegida>
                <ConversionesMobileHome />
              </RutaProtegida>
          } />

          <Route path="/conversiones/m/:stageKey" element={
            <RutaProtegida>
                <ConversionesMobileStage />
              </RutaProtegida>
          } />

          <Route path="/conversiones/vistas" element={
            <RutaProtegida>
              <Layout>
                <ConversionesVistas />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/conversiones/sesiones" element={
            <RutaProtegida>
              <Layout>
                <ConversionesSesiones />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/conversiones/simulaciones" element={
            <RutaProtegida>
              <Layout>
                <ConversionesSimulaciones />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/conversiones/contrataciones" element={
            <RutaProtegida>
              <Layout>
                <ConversionesContrataciones />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/conversiones/suscripciones" element={
            <RutaProtegida>
              <Layout>
                <ConversionesSuscripciones />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/conversiones/certificados" element={
            <RutaProtegida>
              <Layout>
                <ConversionesCertificados />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/conversiones/prospectos-freelancer" element={
            <RutaProtegida>
              <Layout>
                <ProspectosFreelancerPage />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/clientes" element={
            <RutaProtegida>
              <Layout>
                <ClientsList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/contratantes" element={
            <RutaProtegida>
              <Layout>
                <ContratantesList mode="view" />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/contratantes/manager" element={
            <RutaProtegida>
              <Layout>
                <ContratantesManager />
              </Layout>
            </RutaProtegida>
          } />

          {/* Trazabilidad: el informe CMF y su rastro de accesos.
              Contratantes reutiliza el mismo componente con mode="trazabilidad".
              Auditoría se protege en el backend (/api/pdf/audit-log), no aquí. */}
          <Route path="/trazabilidad/contratantes" element={
            <RutaProtegida>
              <Layout>
                <ContratantesList mode="trazabilidad" />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/trazabilidad/auditoria" element={
            <RutaProtegida>
              <Layout>
                <AuditoriaList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/product_config/productos" element={
            <RutaProtegida>
              <Layout>
                <DivinaProductsList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/product_config/productos/new" element={
            <RutaProtegida>
              <Layout>
                <DivinaProductsForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/product_config/productos/edit/:id" element={
            <RutaProtegida>
              <Layout>
                <DivinaProductsForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/product_config/planes" element={
            <RutaProtegida>
              <Layout>
                <DivinaPlanesList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/product_config/planes/new" element={
            <RutaProtegida>
              <Layout>
                <DivinaPlanesForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/product_config/planes/edit/:id" element={
            <RutaProtegida>
              <Layout>
                <DivinaPlanesForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/product_config/polizas" element={
            <RutaProtegida>
              <Layout>
                <ColectivePolizasList />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/product_config/polizas/new" element={
            <RutaProtegida>
              <Layout>
                <PolizaColectivaWizard />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/product_config/polizas/edit/:id" element={
            <RutaProtegida>
              <Layout>
                <PolizaColectivaWizard />
              </Layout>
            </RutaProtegida>
          } />
                    <Route path="/product_config/contratantes" element={
            <RutaProtegida>
              <Layout>
                <ContratantesColectivosList />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/product_config/contratantes/new" element={
            <RutaProtegida>
              <Layout>
                <ContratantesColectivosForm />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/product_config/contratantes/edit/:id" element={
            <RutaProtegida>
              <Layout>
                <ContratantesColectivosForm />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/product_config/empresas-planes" element={
            <RutaProtegida>
              <Layout>
                <EmpresasPlanesManager />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/editorial/historias" element={
            <RutaProtegida>
              <Layout>
                <HistoriasList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/editorial/historias/new" element={
            <RutaProtegida>
              <Layout>
                <HistoriasForm />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/editorial/historias/edit/:id" element={
            <RutaProtegida>
              <Layout>
                <HistoriasForm />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/configuracion" element={
            <RutaProtegida>
              <Layout>
                <Settings />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/familias" element={
            <RutaProtegida>
              <Layout>
                <FamiliasList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/familias/new" element={
            <RutaProtegida>
              <Layout>
                <FamiliasForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/familias/:id" element={
            <RutaProtegida>
              <Layout>
                <FamiliasForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/planes" element={
            <RutaProtegida>
              <Layout>
                <PlanesList />
              </Layout>
            </RutaProtegida>
          } />
          {/* Ramos (Admin) */}
          <Route path="/admin/ramos" element={
            <RutaProtegida>
              <Layout>
                <RamosList />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/admin/ramos/new" element={
            <RutaProtegida>
              <Layout>
                <RamosForm />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/admin/ramos/:id" element={
            <RutaProtegida>
              <Layout>
                <RamosForm />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/dashboard/plans" element={
            <RutaProtegida>
              <Layout>
                <PlanesList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/planes/new" element={
            <RutaProtegida>
              <Layout>
                <PlanesForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/planes/:id" element={
            <RutaProtegida>
              <Layout>
                <PlanesForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/urls" element={
            <RutaProtegida>
              <Layout>
                <UrlsList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/urls/new" element={
            <RutaProtegida>
              <Layout>
                <UrlsForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/urls/:id" element={
            <RutaProtegida>
              <Layout>
                <UrlsForm />
              </Layout>
            </RutaProtegida>
          } />
          
          {/* Rutas para UF Diaria */}
          <Route path="/admin/uf-diaria" element={
            <RutaProtegida>
              <Layout>
                <UfDiariaList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/uf-diaria/new" element={
            <RutaProtegida>
              <Layout>
                <UfDiariaForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/uf-diaria/:id" element={
            <RutaProtegida>
              <Layout>
                <UfDiariaForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/companias" element={
            <RutaProtegida>
              <Layout>
                <CompaniesList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/companias/new" element={
            <RutaProtegida>
              <Layout>
                <CompanyForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/admin/companias/:id" element={
            <RutaProtegida>
              <Layout>
                <CompanyForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/companies" element={
            <RutaProtegida>
              <Layout>
                <CompaniesList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/companies/new" element={
            <RutaProtegida>
              <Layout>
                <CompanyForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/companies/:id" element={
            <RutaProtegida>
              <Layout>
                <CompanyForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/companies/:id/dashboard" element={
            <RutaProtegida>
              <Layout>
                <CompanyDashboard />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/companies/:id/dashboard/:level" element={
            <RutaProtegida>
              <Layout>
                <CompanyDashboard />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/companies/:id/dashboard/:level/:parentId" element={
            <RutaProtegida>
              <Layout>
                <CompanyDashboard />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/companies/:id/settings" element={
            <RutaProtegida>
              <Layout>
                <CompanyForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments" element={
            <RutaProtegida>
              <Layout>
                <PaymentsList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments/subscriptions" element={
            <RutaProtegida>
              <Layout>
                <SubscriptionsList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/users" element={
            <RutaProtegida>
              <Layout>
                <UsersList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/certificates" element={
            <RutaProtegida>
              <Layout>
                <CertificatesList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/emision/polizas" element={
            <RutaProtegida>
              <Layout>
                <PolizasIndividualesList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/emision/rendiciones" element={
            <RutaProtegida>
              <Layout>
                <RendicionesPage />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/wallet/passes" element={
            <RutaProtegida>
              <Layout>
                <WalletPasses />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/wallet/transmisiones" element={
            <RutaProtegida>
              <Layout>
                <WalletBroadcast />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/pdf-templates" element={
            <RutaProtegida>
              <Layout>
                <PdfTemplateManager />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/ordenes-pago" element={
            <RutaProtegida>
              <Layout>
                <OrdenespagoList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/ordenes-pago/:id" element={
            <RutaProtegida>
              <Layout>
                <OrdenespagoDetails />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/ordenes-pago/nueva" element={
            <RutaProtegida>
              <Layout>
                <ErrorPage />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/ordenes-pago/:id/editar" element={
            <RutaProtegida>
              <Layout>
                <ErrorPage />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/exportar" element={
            <RutaProtegida>
              <Layout>
                <Exportar />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/filemaker-test" element={
            <RutaProtegida>
              <Layout>
                <FileMakerTest />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/app-health" element={
            <RutaProtegida>
              <Layout>
                <AppHealth />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments/virtualpos/plans" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosPlansList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments/virtualpos/plans/new" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosPlansForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments/virtualpos/plans/edit/:id" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosPlansForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments/virtualpos/plans/:id" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosPlanDetail />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments/virtualpos/subscriptions" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosSubscriptionsList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments/virtualpos/subscriptions/new" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosSubscriptionForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments/virtualpos/subscriptions/:id" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosSubscriptionDetail />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/payments/conciliacion" element={
            <RutaProtegida>
              <Layout>
                <ConciliacionList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/payments/conciliacion/:companiaId/:periodo/factura" element={
            <RutaProtegida>
              <Layout>
                <FacturaUpload />
              </Layout>
            </RutaProtegida>
          } />

          {/* YoolPay — el ciclo de liquidación, en DOS pantallas y no diez.
              Mes → Día, y el día es donde se ejecuta. Las otras ocho se borraron:
              eran subconjuntos del ciclo, herramientas de diagnóstico o
              configuración conviviendo al mismo nivel, y había que adivinar cuál
              miraba lo mismo. */}
          <Route path="/yoolpay/mes" element={
            <RutaProtegida>
              <Layout>
                <YoolPayMes />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/yoolpay/dia" element={
            <RutaProtegida>
              <Layout>
                <YoolPayDia />
              </Layout>
            </RutaProtegida>
          } />

          {/* YoolPay — Tasas de Comisión (fee_definitions)
              Sección separada del ciclo Mes/Día. Administración de las tarifas
              del PSP que se leen en la liquidación. */}
          <Route path="/yoolpay/fees" element={
            <RutaProtegida>
              <Layout>
                <YoolPayFeeDefinitionsList />
              </Layout>
            </RutaProtegida>
          } />

          {/* El informe que se le manda a la compañía. Ruta propia y no un panel dentro
              del día: es un documento para un tercero, con su propio @media print, y
              mezclarlo con la vista operativa obligaría a esconder media pantalla al
              imprimir. Sólo lectura — GET /api/informe no tiene POST. */}
          <Route path="/yoolpay/informe/dia" element={
            <RutaProtegida>
              <Layout>
                <InformeLiquidacion tipo="dia" />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/yoolpay/informe/mes" element={
            <RutaProtegida>
              <Layout>
                <InformeLiquidacion tipo="mes" />
              </Layout>
            </RutaProtegida>
          } />

          {/* Las rutas viejas no se dejan muertas: un bookmark o un link pegado en
              un chat aterrizaría en la pantalla en blanco del router, que se lee
              como «se cayó» y no como «esto se movió». Van al mes, que es la
              entrada del ciclo. */}
          <Route path="/yoolpay/cierre-periodo" element={<Navigate to="/yoolpay/mes" replace />} />
          <Route path="/yoolpay/liquidaciones" element={<Navigate to="/yoolpay/mes" replace />} />
          <Route path="/yoolpay/cierre" element={<Navigate to="/yoolpay/mes" replace />} />
          <Route path="/yoolpay/conciliacion" element={<Navigate to="/yoolpay/mes" replace />} />
          <Route path="/yoolpay/movements" element={<Navigate to="/yoolpay/mes" replace />} />
          <Route path="/yoolpay/cuotas" element={<Navigate to="/yoolpay/mes" replace />} />
          <Route path="/yoolpay/cxc" element={<Navigate to="/yoolpay/mes" replace />} />
          <Route path="/yoolpay/fees" element={<Navigate to="/yoolpay/mes" replace />} />
          <Route path="/yoolpay/plan-pruebas" element={<Navigate to="/yoolpay/mes" replace />} />

          <Route path="/campanas" element={
            <RutaProtegida>
              <Layout>
                <CampaignsList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/new" element={
            <RutaProtegida>
              <Layout>
                <CampaignsForm />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/single" element={
            <RutaProtegida>
              <Layout>
                <SingleMailForm />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/marketing/agent" element={
            <RutaProtegida>
              <Layout>
                <ContentAgent />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/marketing/lead-qualifier" element={
            <RutaProtegida>
              <Layout>
                <LeadQualifier />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/marketing/conversational-sales-agent" element={
            <RutaProtegida>
              <Layout>
                <ConversationalSalesAgentRoute />
              </Layout>
            </RutaProtegida>
          } />


          <Route path="/campanas/:id" element={
            <RutaProtegida>
              <Layout>
                <CampaignDetail />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/:id/edit" element={
            <RutaProtegida>
              <Layout>
                <CampaignsFormEdit />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/templates" element={
            <RutaProtegida>
              <Layout>
                <TemplatesList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/templates/new" element={
            <RutaProtegida>
              <Layout>
                <TemplatesForm />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/templates/:id" element={
            <RutaProtegida>
              <Layout>
                <TemplatePreview />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/templates/:id/edit" element={
            <RutaProtegida>
              <Layout>
                <TemplatesFormEdit />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/contacts" element={
            <RutaProtegida>
              <Layout>
                <ContactsList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/contacts/new" element={
            <RutaProtegida>
              <Layout>
                <ContactsForm />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/contacts/:id" element={
            <RutaProtegida>
              <Layout>
                <ContactDetail />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/contacts/:id/edit" element={
            <RutaProtegida>
              <Layout>
                <ContactsFormEdit />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/segments" element={
            <RutaProtegida>
              <Layout>
                <SegmentsList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/segments/new" element={
            <RutaProtegida>
              <Layout>
                <SegmentsForm />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/segments/:id" element={
            <RutaProtegida>
              <Layout>
                <SegmentDetail />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/segments/:id/edit" element={
            <RutaProtegida>
              <Layout>
                <SegmentsFormEdit />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/campanas/contacts/:id/segments" element={
            <RutaProtegida>
              <Layout>
                <ContactSegmentManager />
              </Layout>
            </RutaProtegida>
          } />

            <Route path="/email" element={
                        <RutaProtegida>
                            <Layout>
                                <EmailClient />
                            </Layout>
                        </RutaProtegida>
                    } />

          <Route path="/conversaciones" element={
            <RutaProtegida>
              <Layout>
                <ConversacionesList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/conversaciones/:id" element={
            <RutaProtegida>
              <Layout>
                <ConversacionDetalle />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/socials/instagram/insights" element={
            <RutaProtegida>
              <Layout>
                <InstagramInsightsPage />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/dev/api-docs" element={
            <RutaProtegida>
              <Layout>
                <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Cargando documentación…</div>}>
                  <ApiDocsPage />
                </Suspense>
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments-admin/virtualpos" element={
            <RutaProtegida>
              <Navigate to="/payments-admin/virtualpos/planes" replace />
            </RutaProtegida>
          } />

          <Route path="/payments-admin/virtualpos/planes" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosPlansList />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments-admin/virtualpos/planes/new" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosPlansForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments-admin/virtualpos/planes/edit/:id" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosPlansForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments-admin/virtualpos/planes/:id" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosPlanDetail />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments-admin/virtualpos/subscriptions" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosSubscriptionsList />
              </Layout>
            </RutaProtegida>
          } />

          <Route path="/payments-admin/virtualpos/subscriptions/new" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosSubscriptionForm />
              </Layout>
            </RutaProtegida>
          } />
          
          <Route path="/payments-admin/virtualpos/subscriptions/:id" element={
            <RutaProtegida>
              <Layout>
                <VirtualPosSubscriptionDetail />
              </Layout>
            </RutaProtegida>
          } />

          {/* Los cargos de suscripción y los logs de cron NO son de VirtualPOS: la lista
              no filtra por `payment_provider` y los logs son de todos los crons. Vivían
              bajo /virtualpos/ de cuando era el único PSP. Las rutas viejas quedan abajo
              como redirect. */}
          <Route path="/payments-admin/cargos" element={
            <RutaProtegida>
              <Layout>
                <SubscriptionChargesList />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/payments-admin/cargos/:id" element={
            <RutaProtegida>
              <Layout>
                <SubscriptionChargeDetailPage />
              </Layout>
            </RutaProtegida>
          } />
          <Route path="/payments-admin/cron/logs" element={
            <RutaProtegida>
              <Layout>
                <CronRunLogs />
              </Layout>
            </RutaProtegida>
          } />

          {/* DEPRECADAS — rutas viejas bajo /virtualpos/. Se mantienen sólo para los
              enlaces que la gente tenga guardados; `replace` evita que queden en el
              historial del navegador. Retirar cuando dejen de recibir tráfico. */}
          <Route path="/payments-admin/virtualpos/cargos" element={<Navigate to="/payments-admin/cargos" replace />} />
          <Route path="/payments-admin/virtualpos/cargos/:id" element={<RedirectCargoDetalle />} />
          <Route path="/payments-admin/virtualpos/cron/logs" element={<Navigate to="/payments-admin/cron/logs" replace />} />
          
          {/* URL desconocida: con sesión muestra el 404, sin ella manda al login.
              Antes eran dos <Route path="*"> y <Routes> solo renderiza el primero,
              así que el ErrorPage era inalcanzable: un usuario con sesión que caía
              en una URL mala veía el <SignedOut> del primero, o sea nada. */}
          <Route path="*" element={
            <RutaProtegida>
              <Layout>
                <ErrorPage />
              </Layout>
            </RutaProtegida>
          } />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

function App() {
  const clerkPublishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;
  const [clerkLoadTimeoutReached, setClerkLoadTimeoutReached] = useState(false);

  const isIpHost = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const host = window.location.hostname;
    return /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
  }, []);

  const clerkJSVersion = useMemo(() => {
    return '5.119.1';
  }, []);

  const clerkJSUrl = useMemo(() => {
    if (!isIpHost) return undefined;
    return `https://cdn.jsdelivr.net/npm/@clerk/clerk-js@${clerkJSVersion}/dist/clerk.browser.js`;
  }, [clerkJSVersion, isIpHost]);

  const clerkProxyUrl = useMemo(() => {
    if (!isIpHost) return undefined;
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (backendUrl) return `${backendUrl.replace(/\/$/, '')}/__clerk`;
    return `${window.location.protocol}//${window.location.hostname}:3011/__clerk`;
  }, [isIpHost]);
  
  useEffect(() => {
    if (!isIpHost) return;
    if (window.Clerk?.loaded === true) {
      setClerkLoadTimeoutReached(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      if (window.Clerk?.loaded !== true) {
        setClerkLoadTimeoutReached(true);
      }
    }, 8000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isIpHost]);

  if (!clerkPublishableKey) {
    console.error('REACT_APP_CLERK_PUBLISHABLE_KEY no está definida');
    return <div>Error de configuración</div>;
  }

  if (isIpHost && clerkLoadTimeoutReached && window.Clerk?.loaded !== true) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 p-6">
          <div className="text-gray-900 font-semibold text-lg mb-2">Error de conexión con el servicio de autenticación</div>
          <div className="text-gray-600 text-sm mb-4">
            No se pudo inicializar Clerk desde esta URL: <span className="font-mono">{window.location.origin}</span>
          </div>
          <div className="text-gray-600 text-sm mb-6">
            Si necesitas abrir la app desde una IP (192.168.x.x), usa HTTPS o configura un proxy de Clerk en un dominio con SSL.
          </div>
          <button
            type="button"
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
            onClick={() => window.location.href = window.location.href}
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ClerkProvider publishableKey={clerkPublishableKey} clerkJSUrl={clerkJSUrl} clerkJSVersion={clerkJSVersion} proxyUrl={clerkProxyUrl} signInUrl="/sign-in" signInFallbackRedirectUrl="/dashboard">
          <AppContent />
          <Toaster />
        </ClerkProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
